// ============================================
// pages/transportation/TransportSlabsPage.tsx
// Transport rate slabs master (distance-based rates).
// TransportTripForm looks up rates from this table by trip km.
// ============================================
import React, { useState, useEffect } from 'react';
import { Layers, Loader2, Plus, Edit2, Trash2, Save, X, CheckCircle, XCircle, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { commoditiesAPI, transportSlabsAPI, importAPI } from '../../api/services';
import { MasterImportBar } from '../../components/MasterImportBar';
import { UserRole, num } from '../../types/markfed';

const emptyForm = {
  slab_name: '',
  commodity_id: 0,
  min_km: 0,
  max_km: 9999,
  rate_per_qtl: null as number | null,
  rate_per_km_per_qtl: null as number | null,
  effective_from: '',
  effective_to: '',
  remarks: '',
  is_active: true,
};

export const TransportSlabsPage: React.FC = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(UserRole.MD, UserRole.AO_CAO, UserRole.SUPER_ADMIN);

  const [commodities, setCommodities] = useState<any[]>([]);
  const [slabs, setSlabs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<any>(emptyForm);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Rate calculator
  const [calcKm, setCalcKm] = useState<number>(0);
  const [calcCommodity, setCalcCommodity] = useState<number>(0);
  const [calcResult, setCalcResult] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([commoditiesAPI.list(), transportSlabsAPI.list()]);
      setCommodities(c.data);
      setSlabs(Array.isArray(s.data) ? s.data : []);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (s: any) => {
    setEditingId(s.id);
    setForm({
      slab_name: s.slab_name || '',
      commodity_id: s.commodity_id || 0,
      min_km: num(s.min_km),
      max_km: num(s.max_km),
      rate_per_qtl: s.rate_per_qtl != null ? num(s.rate_per_qtl) : null,
      rate_per_km_per_qtl: s.rate_per_km_per_qtl != null ? num(s.rate_per_km_per_qtl) : null,
      effective_from: s.effective_from || '',
      effective_to: s.effective_to || '',
      remarks: s.remarks || '',
      is_active: !!s.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.slab_name?.trim()) {
      setMessage({ type: 'error', text: 'Slab name is required' });
      return;
    }
    if (form.rate_per_qtl == null && form.rate_per_km_per_qtl == null) {
      setMessage({ type: 'error', text: 'Either flat rate/qtl or rate/km/qtl must be set' });
      return;
    }
    if (form.rate_per_qtl != null && form.rate_per_km_per_qtl != null) {
      setMessage({ type: 'error', text: 'Set only one: flat rate/qtl OR rate/km/qtl' });
      return;
    }
    try {
      const payload = {
        ...form,
        commodity_id: form.commodity_id || null,
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
      };
      if (editingId) {
        await transportSlabsAPI.update(editingId, payload);
        setMessage({ type: 'success', text: 'Updated' });
      } else {
        await transportSlabsAPI.create(payload);
        setMessage({ type: 'success', text: 'Created' });
      }
      setShowModal(false);
      await load();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save' });
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete slab "${name}"?`)) return;
    try {
      await transportSlabsAPI.remove(id);
      setSlabs((prev) => prev.filter((s) => s.id !== id));
      setMessage({ type: 'success', text: 'Deleted' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to delete' });
    }
  };

  const handleLookup = async () => {
    try {
      const res = await transportSlabsAPI.lookup(calcKm, calcCommodity || undefined);
      setCalcResult(res.data);
    } catch (err: any) {
      setCalcResult({ found: false, error: err?.response?.data?.message || 'Lookup failed' });
    }
  };

  if (loading && slabs.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Layers className="w-7 h-7 text-blue-600" />
            Transport Rate Slabs
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Distance-based rate master — auto-applied by TransportTripForm on km entry
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <MasterImportBar
              label="Import Slabs"
              templatePath="/import/transport-slabs/template"
              uploader={(f) => importAPI.transportSlabs(f)}
              onImported={async () => {
                const res = await transportSlabsAPI.list();
                setSlabs(Array.isArray(res.data) ? res.data : []);
              }}
              onMessage={setMessage}
            />
            <button onClick={openAdd}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm">
              <Plus className="w-4 h-4" />Add Slab
            </button>
          </div>
        )}
      </div>

      {/* Rate calculator */}
      <div className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Search className="w-4 h-4" />Rate Lookup Calculator
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Check what rate TransportTripForm will auto-apply for a given distance.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Distance (km)</label>
            <input type="number" step="0.01" value={calcKm || ''}
              onChange={(e) => setCalcKm(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Commodity (optional)</label>
            <select value={calcCommodity} onChange={(e) => setCalcCommodity(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>-- Any / Universal --</option>
              {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <button onClick={handleLookup}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm">
            Lookup
          </button>
          {calcResult && (
            <div className="text-xs">
              {calcResult.found ? (
                <div>
                  <div className="text-gray-600">Matched: <span className="font-semibold">{calcResult.slab.slab_name}</span></div>
                  <div className="text-green-700 font-bold text-base">Rs {num(calcResult.effective_rate_per_qtl).toFixed(4)} / qtl</div>
                </div>
              ) : (
                <div className="text-red-600">No slab matches {calcKm} km</div>
              )}
            </div>
          )}
        </div>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">#</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Slab Name</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Commodity</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Min KM</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Max KM</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Flat Rs/qtl</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Rs/km/qtl</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Effective</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Active</th>
                {canEdit && <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {slabs.length === 0 ? (
                <tr><td colSpan={canEdit ? 10 : 9} className="px-4 py-12 text-center text-gray-400">
                  No slabs defined. {canEdit && 'Click "Add Slab" to create one.'}
                </td></tr>
              ) : slabs.map((s, i) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                  <td className="px-3 py-2 font-medium text-gray-900">{s.slab_name}</td>
                  <td className="px-3 py-2 text-gray-600">{s.commodity?.name || <span className="italic text-gray-400">Universal</span>}</td>
                  <td className="px-3 py-2 text-right">{num(s.min_km).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">{num(s.max_km).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">{s.rate_per_qtl != null ? num(s.rate_per_qtl).toFixed(4) : '--'}</td>
                  <td className="px-3 py-2 text-right">{s.rate_per_km_per_qtl != null ? num(s.rate_per_km_per_qtl).toFixed(4) : '--'}</td>
                  <td className="px-3 py-2 text-gray-600">
                    {s.effective_from || '—'}
                    {s.effective_to && <span className="text-gray-400"> → {s.effective_to}</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {s.is_active ? <CheckCircle className="w-4 h-4 text-green-600 inline" /> : <XCircle className="w-4 h-4 text-gray-400 inline" />}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(s)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-3 h-3" /></button>
                        <button onClick={() => handleDelete(s.id, s.slab_name)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">{editingId ? 'Edit Slab' : 'Add Slab'}</h2>
                <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Slab Name</label>
                  <input type="text" value={form.slab_name} onChange={(e) => setForm({ ...form, slab_name: e.target.value })}
                    placeholder="e.g., Local (≤1 km), Medium (2-30 km), Long (30+ km)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Commodity (optional — leave blank for universal)</label>
                  <select value={form.commodity_id || 0} onChange={(e) => setForm({ ...form, commodity_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value={0}>-- Universal (any commodity) --</option>
                    {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Min KM</label>
                    <input type="number" step="0.01" value={form.min_km || ''}
                      onChange={(e) => setForm({ ...form, min_km: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Max KM</label>
                    <input type="number" step="0.01" value={form.max_km || ''}
                      onChange={(e) => setForm({ ...form, max_km: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                  </div>
                </div>
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
                  Set <strong>exactly one</strong> of the two rates below:
                  <ul className="list-disc ml-4 mt-1">
                    <li><strong>Flat Rs/qtl</strong> — for short/local trips (rate is independent of distance)</li>
                    <li><strong>Rate Rs/km/qtl</strong> — for longer trips (rate × km × qtl)</li>
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Flat Rate (Rs/qtl)</label>
                    <input type="number" step="0.0001" value={form.rate_per_qtl ?? ''}
                      onChange={(e) => setForm({ ...form, rate_per_qtl: e.target.value ? parseFloat(e.target.value) : null })}
                      placeholder="e.g., 33.33"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Rate (Rs/km/qtl)</label>
                    <input type="number" step="0.0001" value={form.rate_per_km_per_qtl ?? ''}
                      onChange={(e) => setForm({ ...form, rate_per_km_per_qtl: e.target.value ? parseFloat(e.target.value) : null })}
                      placeholder="e.g., 1.99"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Effective From</label>
                    <input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Effective To</label>
                    <input type="date" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                  <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded" />
                  Active
                </label>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={handleSave}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg">
                  <Save className="w-4 h-4" />Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TransportSlabsPage;
